import {
  getWidthPreset,
  presetForViewportWidth,
  type ViewerWidthPresetId,
} from '@/features/viewer/width-presets';

/**
 * The offer to adopt the width a thread was pinned at (spec R3.6).
 *
 * A pin is only in the right place at the width its author was looking at: a
 * button that sits beside a heading at 1440 sits under it at 390, and the
 * selector resolves to a genuinely different box. So selecting a thread offers
 * to switch — an *offer*, never an action, because the reviewer may be
 * deliberately checking how the same pin lands at another width, and yanking
 * the frame out from under them would make that impossible.
 *
 * ============================================================================
 * WHY IT REMEMBERS
 * ============================================================================
 * Declining is a decision about a thread, not about a moment. A reviewer
 * reading a long thread clicks its row, scrolls the panel, clicks it again;
 * without memory they would be asked the same question every time and the only
 * way to stop it would be to accept an answer they had already refused. So a
 * thread that has been offered once is never offered again for the life of the
 * viewer — accepted or declined, because accepting it makes the offer moot
 * anyway.
 *
 * The rule is pure and the remembering is a plain list of ids in the viewer
 * store, which is cleared on a version switch along with everything else that
 * names something inside the old version.
 */

export interface ViewportOfferInput {
  /** The thread being selected. */
  readonly threadId: string;
  /** The frame width recorded on the thread when its pin was placed. */
  readonly threadViewportWidth: number;
  /** The preset the viewer is currently framing at. */
  readonly currentPreset: ViewerWidthPresetId;
  /** Threads already offered this session, accepted or declined. */
  readonly offeredThreadIds: readonly string[];
}

export interface ViewportOffer {
  /** The preset that best represents the recorded width. */
  readonly preset: ViewerWidthPresetId;
  /** Its label, for the prompt: "Switch to Mobile". */
  readonly label: string;
}

/**
 * The offer to make, or `null` for "say nothing".
 *
 * Nothing is said when the recorded width already maps to the preset on screen
 * — including the common case of a thread created at fit-to-window in a window
 * that happened to be laptop-sized, which `presetForViewportWidth` resolves to
 * `laptop`. Offering to switch to the width you are already at is noise that
 * teaches the reviewer to dismiss the prompt without reading it.
 */
export const viewportSwitchOffer = ({
  threadId,
  threadViewportWidth,
  currentPreset,
  offeredThreadIds,
}: ViewportOfferInput): ViewportOffer | null => {
  if (offeredThreadIds.includes(threadId)) {
    return null;
  }

  const preset = presetForViewportWidth(threadViewportWidth);

  if (preset === currentPreset) {
    return null;
  }

  return { preset, label: getWidthPreset(preset).label };
};
